import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Zap, Globe, Sparkles, Award, TrendingUp, Rocket, Shield, Users, Lightbulb, CheckCircle, Star } from 'lucide-react';
import ServiceContactForm from '../components/ServiceContactForm';

const About = () => {
  const stats = [
    { value: '250+', label: 'Projects Delivered', icon: Award },
    { value: '50+', label: 'Happy Clients', icon: Heart },
    { value: '15+', label: 'Years Experience', icon: TrendingUp },
    { value: '98%', label: 'Client Satisfaction', icon: Sparkles }
  ];

  const values = [
    {
      icon: Target,
      title: 'Innovation First',
      description: 'We push boundaries and embrace cutting-edge technologies to deliver solutions that stand out in the market.'
    },
    {
      icon: Heart,
      title: 'Client-Centric',
      description: 'Your success is our success. We build lasting partnerships based on trust, transparency, and exceptional results.'
    },
    {
      icon: Zap,
      title: 'Agile Excellence',
      description: 'Fast-paced delivery without compromising quality. We adapt quickly to changing needs and market demands.'
    },
    {
      icon: Globe,
      title: 'Global Mindset',
      description: 'Local expertise with a global perspective. We create solutions that scale across markets and cultures.'
    }
  ];

  const expertise = [
    {
      icon: Rocket,
      title: 'Digital Transformation',
      description: 'End-to-end digital solutions that modernize your business',
      stats: '50+ transformations'
    },
    {
      icon: Lightbulb,
      title: 'Innovation Strategy',
      description: 'Forward-thinking approaches that keep you ahead',
      stats: '100+ innovations'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade security for all our solutions',
      stats: '99.9% uptime'
    },
    {
      icon: Users,
      title: 'Customer Success',
      description: 'Dedicated support ensuring your success',
      stats: '24/7 support'
    }
  ];

  const achievements = [
    {
      year: '2024',
      title: 'Best Digital Agency Award',
      organization: 'Tech Innovation Awards',
      icon: Star
    },
    {
      year: '2023',
      title: 'Top 100 Fast-Growing Companies',
      organization: 'Business Excellence Magazine',
      icon: TrendingUp
    },
    {
      year: '2023',
      title: 'Excellence in UI/UX Design',
      organization: 'Design Masters Association',
      icon: Award
    },
    {
      year: '2022',
      title: 'Best AI Implementation',
      organization: 'AI & Tech Summit',
      icon: Rocket
    }
  ];

  const certifications = [
    'AWS Certified Solutions Architect',
    'Google Cloud Professional',
    'Microsoft Azure Expert',
    'Certified Scrum Master',
    'ISO 27001 Certified',
    'PCI DSS Compliant'
  ];

  return (
    <div className="bg-white pt-16">
      {/* Hero Section */}
      <section className="relative py-32 px-8 lg:px-16 overflow-hidden bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 mb-6 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20"
            >
              <Sparkles size={20} className="text-light-pink" />
              <span className="text-sm font-mono uppercase tracking-wider text-white">About SYNCORE LLC</span>
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              Crafting Digital
              <br />
              <span className="bg-gradient-to-r from-light-pink to-light-blue bg-clip-text text-transparent">
                Excellence Since 2010
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed mb-8">
              We're not just a digital agency—we're your innovation partner. Transforming visionary ideas into extraordinary digital experiences that drive real business growth.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="px-8 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold"
              >
                🚀 Innovation Driven
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="px-8 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold"
              >
                🎯 Results Focused
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="px-8 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold"
              >
                💡 Creative Solutions
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-8 lg:px-16 bg-gradient-to-br from-grey via-white to-grey">
        <div className="max-w-8xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-light-pink to-mid-purple mb-4 group-hover:scale-110 transition-transform">
                  <stat.icon size={32} className="text-white" />
                </div>
                <div className="text-5xl md:text-6xl font-bold text-black mb-2" style={{ fontWeight: 700 }}>{stat.value}</div>
                <div className="text-sm md:text-base font-semibold text-dark-grey uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative h-[500px] rounded-3xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=1000&fit=crop"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-8 left-8 text-white">
                  <p className="text-2xl font-bold">Empowering Innovation</p>
                  <p className="text-white/80">One Project at a Time</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-light-pink flex items-center justify-center">
                    <Target size={24} className="text-black" />
                  </div>
                  <h3 className="text-3xl font-bold">Our Mission</h3>
                </div>
                <p className="text-lg text-dark-grey leading-relaxed">
                  To empower businesses with innovative digital solutions that drive growth, enhance user experiences, and create lasting impact in the digital landscape.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-mid-blue flex items-center justify-center">
                    <Eye size={24} className="text-white" />
                  </div>
                  <h3 className="text-3xl font-bold">Our Vision</h3>
                </div>
                <p className="text-lg text-dark-grey leading-relaxed">
                  To be the world's most trusted digital innovation partner, recognized for transforming ambitious ideas into market-leading digital products that shape the future.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 px-8 lg:px-16 bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a] text-white">
        <div className="max-w-8xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
              What <span className="text-light-pink">Drives</span> Us
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Our core values shape every project we undertake and every relationship we build
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-light-pink transition-all hover:shadow-2xl hover:shadow-light-pink/20"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-light-pink to-mid-purple flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <value.icon size={28} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{value.title}</h3>
                <p className="text-white/70 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-24 px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
              Our
              <br />
              <span className="bg-gradient-to-r from-mid-purple to-light-pink bg-clip-text text-transparent">
                Expertise
              </span>
            </h2>
            <p className="text-xl text-dark-grey max-w-3xl mx-auto">
              Comprehensive capabilities that drive your digital success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {expertise.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-grey hover:border-light-pink hover:shadow-2xl transition-all group"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-light-pink/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <item.icon size={32} className="text-light-pink" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                    <p className="text-dark-grey mb-3">{item.description}</p>
                    <div className="inline-block px-4 py-2 rounded-full bg-grey text-sm font-semibold text-mid-purple">
                      {item.stats}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="py-24 px-8 lg:px-16 bg-gradient-to-br from-grey via-white to-grey">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
              Awards &
              <br />
              <span className="bg-gradient-to-r from-mid-purple to-light-pink bg-clip-text text-transparent">
                Recognition
              </span>
            </h2>
            <p className="text-xl text-dark-grey max-w-3xl mx-auto">
              Recognized for excellence and innovation in the digital space
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-grey hover:shadow-xl transition-all group"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-light-pink to-mid-purple flex items-center justify-center flex-shrink-0">
                    <achievement.icon size={28} className="text-white" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-light-pink mb-2">{achievement.year}</div>
                    <h3 className="text-xl font-bold mb-2">{achievement.title}</h3>
                    <p className="text-dark-grey text-sm">{achievement.organization}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Certifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16"
          >
            <h3 className="text-2xl font-bold text-center mb-8">Certifications & Partnerships</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-white rounded-xl p-4 border border-grey hover:border-light-pink transition-all text-center group"
                >
                  <CheckCircle size={24} className="mx-auto mb-2 text-light-pink" />
                  <p className="text-xs font-semibold text-dark-grey group-hover:text-black transition-colors">
                    {cert}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <ServiceContactForm serviceName="General Inquiry" />
    </div>
  );
};

export default About;
